import type { ReactNode } from 'react'

import {
    Bar,
    BarChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts'
import ChartTooltip from '@/components/charts/chart-tooltip'
import ChartLegend from '@/components/charts/chart-legend'

export interface ChartSeries {
    key: string
    label: string
    color: string
}

interface ColumnChartProps {
    data: Array<Record<string, string | number>>
    xKey: string
    series: ChartSeries[]
    ariaLabel: string
    height?: number
    valueFormatter?: (value: number) => string
    tickFormatter?: (value: number) => string
    tooltipLabelKey?: string
}

const AXIS_TICK = { fill: 'var(--muted-foreground)', fontSize: 12 }

// Vertical columns for counts or amounts over time. Two series render side by side.
const ColumnChart = ({
    data,
    xKey,
    series,
    ariaLabel,
    height = 260,
    valueFormatter,
    tickFormatter,
    tooltipLabelKey
}: ColumnChartProps): ReactNode => {
    return (
        <figure className='grid gap-3'>
            {series.length > 1 && <ChartLegend series={series} />}

            <div style={{ height }} aria-hidden='true'>
                <ResponsiveContainer width='100%' height='100%'>
                    <BarChart accessibilityLayer={false} data={data} margin={{ top: 8, right: 4, bottom: 0, left: -12 }} barGap={2} barCategoryGap='22%'>
                        <CartesianGrid vertical={false} stroke='var(--chart-grid)' />
                        <XAxis
                            dataKey={xKey}
                            tickLine={false}
                            axisLine={{ stroke: 'var(--chart-axis)' }}
                            tick={AXIS_TICK}
                            interval='preserveStartEnd'
                            minTickGap={12}
                            dy={6}
                        />
                        <YAxis
                            allowDecimals={false}
                            tickLine={false}
                            axisLine={false}
                            tick={AXIS_TICK}
                            width={56}
                            tickFormatter={tickFormatter}
                        />
                        <Tooltip
                            cursor={{ fill: 'var(--muted)', opacity: 0.7 }}
                            content={<ChartTooltip valueFormatter={valueFormatter} labelKey={tooltipLabelKey} />}
                        />
                        {series.map(item => (
                            <Bar
                                key={item.key}
                                dataKey={item.key}
                                name={item.label}
                                fill={item.color}
                                radius={[4, 4, 0, 0]}
                                maxBarSize={24}
                                isAnimationActive={false}
                            />
                        ))}
                    </BarChart>
                </ResponsiveContainer>
            </div>

            <table className='sr-only'>
                <caption>{ariaLabel}</caption>
                <thead>
                    <tr>
                        <th scope='col'>{xKey}</th>
                        {series.map(item => <th key={item.key} scope='col'>{item.label}</th>)}
                    </tr>
                </thead>
                <tbody>
                    {data.map((row, index) => (
                        <tr key={index}>
                            <th scope='row'>{String(row[tooltipLabelKey ?? xKey] ?? row[xKey])}</th>
                            {series.map(item => (
                                <td key={item.key}>
                                    {valueFormatter ? valueFormatter(Number(row[item.key] ?? 0)) : String(row[item.key] ?? 0)}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </figure>
    )
}

export default ColumnChart
